import { useState, useEffect } from "react";
import { User } from "../types/user.types";
import { listUsers } from "../services/userService";
import { useAuth } from "../../../context/AuthContext";
import { isSuperAdmin } from "../../../utils/permission";
import { debug } from "../../../utils/debugLogger";

interface UseAllUsersReturn {
  users: User[];
  loading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  canManageUsers: boolean;
}

const useAllUsers = (): UseAllUsersReturn => {
  const { idToken, userGroups } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Check if user has permission to manage users
  const canManageUsers = isSuperAdmin(userGroups);

  useEffect(() => {
    if (idToken && canManageUsers) {
      fetchUsers();
    } else if (!canManageUsers) {
      setError("You don't have permission to access this page");
      setLoading(false);
    }
  }, [idToken, canManageUsers]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const allUsers = await listUsers(idToken);
      setUsers(allUsers || []);
    } catch (err: any) {
      debug.error("Error fetching users:", err);
      setError("Failed to fetch users: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  return {
    users,
    loading,
    error,
    fetchUsers,
    canManageUsers
  };
};

export default useAllUsers;